import React from 'react';
import {useQuery, gql} from '@apollo/client';
import {Box, capitalize, Grid, Typography, Button} from '@mui/material';

import {ReviewAllowedActionsType} from '../../schema-types/globalTypes';
import {
  GetInvitationEmailAudit,
  GetInvitationEmailAuditVariables,
} from '../../schema-types/GetInvitationEmailAudit';
import {INVITATION_EMAIL_AUDIT_FRAGMENT} from '../../graphql/fragments';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import {InvitationContactDisplay} from './components/InvitationContactDisplay';
import {VulnerableClientBanner} from '../../components/VulnerableClientBanner/VulnerableClientBanner';
import {themeColors} from '../../theme/Theme';
import {InviteButton, SendECQButton, SendRTQButton} from './InvitationActions';
import {useReviewInvitation} from './hooks/useReviewInvitation';
import {ContactRAGStatusesFragment} from '../../schema-types/ContactRAGStatusesFragment';

// Helper function to map status display text
const getStatusDisplayText = (status: string): string => {
  if (status.toLowerCase() === 'scheduled') {
    return 'Booked For';
  }
  return capitalize(status);
};

// Types using GraphQL fragments
interface SingleInvitationProps {
  reviewId: string;
}

// GraphQL Queries
export const GET_INVITATION_EMAIL_AUDIT = gql`
  query GetInvitationEmailAudit($reviewId: UUID!) {
    getInvitationEmailAudit(reviewId: $reviewId) {
      ...InvitationEmailAuditFragment
    }
  }
  ${INVITATION_EMAIL_AUDIT_FRAGMENT}
`;

// Helper function for date formatting
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-GB');
};

const formatDateTimeDetailed = (dateString: string) => {
  const date = new Date(dateString);
  return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

// Navigation functions using window.open
const openAccount = (accountId: string) => {
  window.open(`/account/${accountId}`, '_blank');
};

const openReview = (reviewId: string, accountId?: string) => {
  if (accountId) {
    window.open(`/account/${accountId}/review-journey/${reviewId}`, '_blank');
  } else {
    console.warn('Cannot open review without accountId');
  }
};

const LoadingContacts: React.FC<{
  isSlowLoading: boolean;
}> = ({isSlowLoading}) => {
  if (isSlowLoading) {
    return <Loading entityName="contact" />;
  }
  return (
    <Typography color="text.secondary" sx={{mt: 2}}>
      No contacts available for this review.
    </Typography>
  );
};

export function SingleInvitation({reviewId}: SingleInvitationProps) {
  const {
    reviewInvitation: review,
    reviewInvitationError: error,
    reviewInvitationLoading: loading,
    reviewInvitationLoadingSlow,
  } = useReviewInvitation(reviewId);

  const {
    data: emailAuditData,
    error: emailAuditError,
    loading: emailAuditLoading,
  } = useQuery<GetInvitationEmailAudit, GetInvitationEmailAuditVariables>(
    GET_INVITATION_EMAIL_AUDIT,
    {
      variables: {reviewId: reviewId as any},
      fetchPolicy: 'network-only',
      skip: !reviewId,
    }
  );

  // const {scheduleInvitation, sendQuestionnaires, isScheduling, isSendingQuestionnaires} =
  //   useInvitationActions();

  const handleOpenLink = (link: string) => {
    window.open(link, '_blank');
  };

  const handleReschedule = () => {
    if (invitation?.rescheduleUrl) {
      handleOpenLink(invitation.rescheduleUrl);
    }
  };

  const handleCancel = () => {
    if (invitation?.cancelUrl) {
      handleOpenLink(invitation.cancelUrl);
    }
  };

  // const handleScheduleInvitation = async () => {
  //   if (data?.reviewDetails?.id) {
  //     await scheduleInvitation(data.reviewDetails.id);
  //   }
  // };

  // const handleSendQuestionnaires = async () => {
  //   if (data?.reviewDetails?.id) {
  //     await sendQuestionnaires(data.reviewDetails.id, {
  //       isEcqRequired: true,
  //       isRtqRequired: true,
  //     });
  //   }
  // };

  // Loading state
  if (loading) {
    return <Loading />;
  }

  // Error state
  if (error) {
    return (
      <Grid item xs={12}>
        <Typography color="error">Error loading invitation: {error.message}</Typography>
      </Grid>
    );
  }

  if (!review) {
    return <ErrorMessage error={{message: 'Review not found'}} />;
  }
  const invitation = review?.invitation;
  const status = invitation?.status
    ? invitation.status.toLocaleLowerCase()
    : 'Not Invited';

  // Use backend-driven allowedActions to determine which buttons to show
  const allowedActions = review.allowedActions || [];
  const canSendInvitation = allowedActions.includes(
    ReviewAllowedActionsType.SEND_INVITATION
  );
  const canSendRTQ = allowedActions.includes(ReviewAllowedActionsType.SEND_RTQ);
  const canSendECQ = allowedActions.includes(ReviewAllowedActionsType.SEND_ECQ);
  const canJoinMeeting = allowedActions.includes(ReviewAllowedActionsType.JOIN_MEETING);
  const canRescheduleMeeting = allowedActions.includes(
    ReviewAllowedActionsType.RESCHEDULE_FOR_CLIENT
  );
  const canCancelMeeting = allowedActions.includes(
    ReviewAllowedActionsType.CANCEL_FOR_CLIENT
  );

  return (
    <Box display="flex" gap={4} flexDirection="column">
      <VulnerableClientBanner contacts={review.contacts || []} />
      <Box bgcolor={themeColors.white}>
        <Typography variant="h6" component="h6" fontWeight="bold">
          Annual review
        </Typography>
        <Typography variant="h4" component="h4" fontWeight="bold">
          {review.account?.name}
        </Typography>
      </Box>

      {invitation?.dateBooked && (
        <Box>
          <Box>
            <div>
              <Typography variant="body1" component="p">
                Review booked date:
              </Typography>
            </div>

            <Typography variant="h4" component="h4">
              {formatDateTimeDetailed(invitation.dateBooked)}
            </Typography>
          </Box>
        </Box>
      )}

      <Box>
        <Box display="flex" flexDirection="row" gap={2}>
          <Button
            onClick={() => openAccount(String(review.account?.id))}
            variant="outlined"
            color="primary"
          >
            Open account
          </Button>
          <Button
            onClick={() => openReview(reviewId, review.account?.id)}
            variant="outlined"
            color="primary"
          >
            Open review
          </Button>
          {canSendInvitation && (
            <InviteButton showDisabledButton review={review} includeSendText />
          )}
          {invitation && !invitation.dateBooked && invitation.status === 'INVITED' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (invitation.bookingLink) {
                  handleOpenLink(invitation.bookingLink);
                }
              }}
            >
              Book meeting for client
            </Button>
          )}
          {invitation?.zoomLink && canJoinMeeting && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleOpenLink(invitation.zoomLink!);
              }}
            >
              Join meeting (Zoom)
            </Button>
          )}

          {/* <Button
            variant="contained"
            color="secondary"
            onClick={handleSendQuestionnaires}
            disabled={isSendingQuestionnaires || isDisabled}
          >
            {isSendingQuestionnaires ? 'Sending...' : 'Send Questionnaires'}
          </Button> */}

          {canSendRTQ && (
            <SendRTQButton
              review={review}
              showDisabledButton={false}
              includeSendText
              size="medium"
            />
          )}

          {canSendECQ && (
            <SendECQButton
              review={review}
              showDisabledButton={false}
              includeSendText
              size="medium"
            />
          )}

          {canRescheduleMeeting && (
            <Button variant="outlined" color="primary" onClick={handleReschedule}>
              Reschedule
            </Button>
          )}
          {canCancelMeeting && (
            <Button variant="outlined" color="primary" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={4} md={3} lg={2}>
            <Typography variant="body1" fontWeight="bold">
              Status:
            </Typography>
            <Typography variant="body2">{getStatusDisplayText(status)}</Typography>
          </Grid>
          <Grid item xs={4} md={3} lg={2}>
            <Typography variant="body1" fontWeight="bold">
              Review scheduled date:
            </Typography>
            <Typography variant="body2">{formatDate(review.dateScheduled)}</Typography>
          </Grid>

          {invitation && (
            <Grid item xs={4} md={3} lg={2}>
              <Typography variant="body1" fontWeight="bold">
                Invitation sent date:
              </Typography>
              <Typography variant="body2">
                {invitation.dateSent ? formatDate(invitation.dateSent) : 'N/A'}
              </Typography>
            </Grid>
          )}

          {invitation && (
            <Grid item xs={4} md={3} lg={2}>
              <Typography variant="body1" fontWeight="bold">
                Reminder sent date:
              </Typography>
              <Typography variant="body2">
                {invitation.dateReminderSent
                  ? formatDate(invitation.dateReminderSent)
                  : ''}
              </Typography>
            </Grid>
          )}

          {invitation?.status === 'SCHEDULED' && invitation?.dateConfirmed && (
            <Grid item xs={4} md={3} lg={2}>
              <Typography variant="body1" fontWeight="bold">
                Meeting confirmed date:
              </Typography>
              <Typography variant="body2">
                {formatDate(invitation.dateConfirmed)}
              </Typography>
            </Grid>
          )}

          {(invitation?.status === 'SCHEDULED' ||
            invitation?.status === 'COMPLETED' ||
            invitation?.status === 'INVITED') &&
            invitation?.invitationOwner && (
              <Grid item xs={4} md={3} lg={2}>
                <Typography variant="body1" fontWeight="bold">
                  Meeting host:
                </Typography>
                <Typography variant="body2">
                  {invitation.invitationOwner.name || 'N/A'}
                </Typography>
              </Grid>
            )}
        </Grid>
      </Box>

      {invitation?.phoneNumberMatchesContactsMessage && (
        <Box
          bgcolor="#fff3cd"
          border="1px solid #ffeaa7"
          borderRadius={1}
          p={2}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Typography variant="body2" color="#856404">
            ⚠️ {invitation.phoneNumberMatchesContactsMessage}
          </Typography>
        </Box>
      )}

      <Box>
        <Grid container spacing={2}>
          {review.contacts?.map((reviewContact) => {
            return (
              <Grid
                item
                xs={12}
                md={6}
                p={1}
                key={reviewContact.id}
                sx={{display: 'flex'}}
              >
                <InvitationContactDisplay
                  contact={reviewContact as ContactRAGStatusesFragment}
                  invitation={invitation}
                  accountId={review.account!.id}
                  emailAuditData={emailAuditData}
                  emailAuditLoading={emailAuditLoading}
                  emailAuditError={emailAuditError}
                />
              </Grid>
            );
          }) || (
            <Grid item>
              <LoadingContacts isSlowLoading={reviewInvitationLoadingSlow} />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
